import { createSlice } from "@reduxjs/toolkit";

import { uploadSingleFile } from "./fileUploadThunk";
import type {
	LastFulfilledPayload,
	LastRejectedPayload,
	UploadSingleFileParams,
} from "./types";

const initialState = {
	loading: false,
	inFlightCount: 0,
	error: null as string | null,
	lastFulfilled: null as LastFulfilledPayload,
	lastRejected: null as LastRejectedPayload,
};

const fileUploadSlice = createSlice({
	name: "fileUpload",
	initialState,
	reducers: {
		clearFileUploadError: (state) => {
			state.error = null;
		},
		setFileUploadError: (state, action: { payload: string }) => {
			state.error = action.payload;
		},
		clearLastFulfilled: (state) => {
			state.lastFulfilled = null;
		},
		clearLastRejected: (state) => {
			state.lastRejected = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(uploadSingleFile.pending, (state) => {
				state.inFlightCount += 1;
				state.loading = true;
				state.error = null;
			})
			.addCase(uploadSingleFile.fulfilled, (state, action) => {
				state.inFlightCount = Math.max(0, state.inFlightCount - 1);
				state.loading = state.inFlightCount > 0;
				state.error = null;
				const payload = action.payload;
				const arg = action.meta.arg as UploadSingleFileParams;
				if (payload && typeof payload === "object" && payload.ok) {
					state.lastFulfilled = {
						key: payload.key,
						s3Url: payload.s3Url,
						path: arg.path,
						index: arg.index,
						total: arg.total,
						fileSize: arg.fileSize,
					};
				} else {
					state.lastFulfilled = null;
				}
			})
			.addCase(uploadSingleFile.rejected, (state, action) => {
				state.inFlightCount = Math.max(0, state.inFlightCount - 1);
				state.loading = state.inFlightCount > 0;
				state.error =
					typeof action.payload === "string"
						? action.payload
						: action.error.message || "Upload failed";
				const arg = action.meta.arg as UploadSingleFileParams;
				state.lastRejected = { path: arg.path, index: arg.index };
			});
	},
});

export const {
	clearFileUploadError,
	clearLastFulfilled,
	clearLastRejected,
	setFileUploadError,
} = fileUploadSlice.actions;

export default fileUploadSlice.reducer;
